/**
 * GameApp — the composition root. This is the ONLY place that knows about every
 * layer at once; it wires them together and owns the frame loop. Each layer it
 * touches is hidden behind an interface (Renderer, InputSource, Network, the
 * services), so any one of them can be swapped without changing the others.
 *
 * Frame loop responsibilities (and nothing else):
 *   - drain abstract input -> player intent
 *   - advance the fixed-timestep simulation
 *   - let the network boundary sync (no-op locally)
 *   - update the camera and render
 *   - drive the HUD and the death/replay flow (which routes through the ads
 *     service, which is dormant in v1)
 */
import { BOT_NAMES } from './config/botNames';
import { DEFAULT_CONFIG, type GameConfig, type MinimapCorner } from './config/config';
import { FEATURES } from './config/features';
import { defaultActions } from './core/actions';
import { entityScore } from './core/formulas';
import { lerpScalar } from './core/math/vec2';
import { Simulation } from './core/simulation';
import type { Entity } from './core/types';
import { World } from './core/world';

import { isTouchDevice } from './input/deviceDetect';
import { DesktopInput } from './input/desktopInput';
import { applyFrameToIntent, type InputSource } from './input/inputSource';
import { MobileInput } from './input/mobileInput';
import { ZoomInput } from './input/zoomInput';

import { LocalNetwork } from './net/localNetwork';
import type { Network } from './net/network';

import { Canvas2DRenderer } from './render/canvas2d/canvas2dRenderer';
import { CameraController } from './render/camera';
import type { Renderer } from './render/renderer';

import { StubAdService } from './services/ads/stubAdService';
import type { RewardedAdService } from './services/ads/adService';
import { AnonymousAuthService } from './services/auth/anonymousAuthService';
import type { AuthService } from './services/auth/authService';
import { LocalLeaderboardService } from './services/leaderboard/localLeaderboardService';
import type { LeaderboardService } from './services/leaderboard/leaderboardService';
import { StubBillingService } from './services/payments/stubBillingService';
import type { BillingService } from './services/payments/billingService';
import { StubSkinsService } from './services/skins/skinsService';
import type { SkinsService } from './services/skins/skinsService';

import { AdOverlayPresenter } from './ui/adOverlay';
import { el, hide, show } from './ui/dom';
import { Hud, type HudLeaderboardRow } from './ui/hud';
import { Screens } from './ui/screens';
import { SkinsScreen } from './ui/skinsScreen';

type GameState = 'menu' | 'playing' | 'dead';

const HUD_INTERVAL_MS = 120;
const MAX_FRAME_MS = 100;

export class GameApp {
  private readonly cfg: GameConfig;

  // Boundaries / services.
  private readonly auth: AuthService;
  private readonly billing: BillingService;
  private readonly skins: SkinsService;
  private readonly ads: RewardedAdService;
  private readonly leaderboard: LeaderboardService;
  private readonly network: Network;
  private readonly renderer: Renderer;
  private readonly camera: CameraController;

  // UI.
  private readonly screens: Screens;
  private readonly hud: Hud;
  private readonly skinsScreen: SkinsScreen;

  // Input.
  private readonly canvas: HTMLCanvasElement;
  private readonly input: InputSource;
  private readonly isMobile: boolean;
  /** View-layer zoom input (camera only, never the sim). Null when disabled. */
  private readonly zoomInput: ZoomInput | null;

  // Sim.
  private world: World | null = null;
  private sim: Simulation | null = null;

  // Run state.
  private state: GameState = 'menu';
  private playerName = 'Player';
  private deathCount = 0;
  private lastFrameTime = 0;
  private lastHudTime = 0;
  private running = false;

  // Debug.
  private fps = 0;

  constructor(config: GameConfig = DEFAULT_CONFIG) {
    this.cfg = config;
    this.canvas = el<HTMLCanvasElement>('game-canvas');

    // --- Services behind their boundaries ---
    this.auth = new AnonymousAuthService();
    this.billing = new StubBillingService(FEATURES.payments);
    this.skins = new StubSkinsService(FEATURES.skins, this.billing);
    this.ads = new StubAdService({
      enabled: FEATURES.ads,
      cfg: this.cfg.ads,
      billing: this.billing,
      presenter: new AdOverlayPresenter(),
    });
    this.leaderboard = new LocalLeaderboardService(FEATURES.leaderboard);
    this.network = new LocalNetwork();

    // Device is decided up front so the renderer can pick a touch-safe minimap.
    this.isMobile = FEATURES.mobileControls && isTouchDevice();

    // --- Render + camera ---
    // On touch devices the minimap goes top-left (clear of the bottom joystick
    // and grow/shrink buttons) at the smaller mobile size; desktop uses config.
    const minimapCorner: MinimapCorner = this.isMobile ? 'top-left' : this.cfg.minimap.corner;
    const minimapSize = this.isMobile ? this.cfg.minimap.mobileSize : this.cfg.minimap.size;
    this.renderer = new Canvas2DRenderer(this.canvas, this.cfg, {
      grid: FEATURES.backgroundGrid,
      interpolation: FEATURES.renderInterpolation,
      minimap: FEATURES.minimap,
      minimapCorner,
      minimapSize,
    });
    this.camera = new CameraController(this.cfg, {
      autoZoom: FEATURES.cameraZoom,
      manual: FEATURES.manualZoom,
    });

    // --- UI ---
    this.hud = new Hud({ leaderboardEnabled: FEATURES.leaderboard, collapsible: this.isMobile });
    this.skinsScreen = new SkinsScreen(this.skins, this.billing, () => this.refreshPlayerSkin());
    this.screens = new Screens(
      {
        onPlay: (name) => this.startFromMenu(name),
        onReplay: () => void this.replay(),
        onMenu: () => this.toMenu(),
        onOpenSkins: () => void this.openSkins(),
        onCloseSkins: () => this.screens.hideSkins(),
      },
      { skinsEnabled: FEATURES.skins, lastName: this.auth.currentUser()?.name ?? '' },
    );

    // --- Input scheme (auto-selected by device) ---
    this.input = this.createInput();
    // Manual zoom feeds the camera only (wheel + pinch). Separate from the sim
    // input boundary so it can never affect game logic / determinism.
    this.zoomInput = FEATURES.manualZoom
      ? new ZoomInput(this.canvas, this.cfg.camera.manualZoomStep)
      : null;
  }

  /** Boot: size the canvas, show the menu, start the render loop. */
  start(): void {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    this.onResize();
    // On touch devices, let CSS shift the top-left HUD stats clear of the
    // top-left minimap (keeps everything visible; touch controls untouched).
    if (FEATURES.minimap && this.isMobile) document.body.classList.add('minimap-active');
    this.zoomInput?.start();
    this.screens.showStart();
    this.hud.setHint(
      this.isMobile
        ? 'Joystick to move · GROW to grow · SHRINK to escape · pinch to zoom'
        : 'Click to grow · Right-click / Space to shrink · scroll to zoom',
    );
    this.running = true;
    this.lastFrameTime = performance.now();
    requestAnimationFrame(this.loop);
  }

  private createInput(): InputSource {
    if (this.isMobile) {
      return new MobileInput({
        joystickZone: el('joystick-zone'),
        growButton: el('btn-grow'),
        shrinkButton: el('btn-shrink'),
      });
    }
    return new DesktopInput(this.canvas, this.cfg);
  }

  // ===================== Flow transitions =====================

  private async startFromMenu(name: string): Promise<void> {
    this.playerName = name;
    await this.auth.signInAnonymous(name);
    this.deathCount = 0;
    this.newWorld();
    this.enterPlaying();
  }

  private async replay(): Promise<void> {
    // The replay flow ALWAYS routes through the ad service (dormant in v1).
    await this.ads.maybeShowRewarded(this.deathCount);
    if (!this.world) this.newWorld();
    else this.world.respawnPlayer(this.playerName, this.skins.selectedId());
    this.enterPlaying();
  }

  private toMenu(): void {
    this.state = 'menu';
    this.input.stop();
    this.hud.hide();
    if (this.isMobile) hide(el('touch-controls'));
    this.screens.showStart();
  }

  private newWorld(): void {
    const seed = (Date.now() ^ (performance.now() * 1000)) >>> 0;
    this.world = new World(this.cfg, seed, defaultActions(), {
      names: BOT_NAMES,
      enabled: FEATURES.botNames,
    });
    // Spawn the player first so bot names can avoid the player's typed name.
    this.world.spawnPlayer(this.playerName, this.skins.selectedId());
    this.world.maintainBotPopulation();
    this.sim = new Simulation(this.world);
    const p = this.world.player!;
    this.camera.snapTo(p.pos.x, p.pos.y, p.size);
  }

  private enterPlaying(): void {
    this.screens.hideAll();
    this.hud.show();
    if (this.isMobile) show(el('touch-controls'));
    this.input.start();
    const p = this.world?.player;
    if (p) this.camera.snapTo(p.pos.x, p.pos.y, p.size);
    this.state = 'playing';
  }

  private handleDeath(): void {
    if (this.state !== 'playing') return;
    this.state = 'dead';
    this.deathCount++;
    const world = this.world;
    const player = world?.player;
    const score = world && player ? entityScore(player, world.time, this.cfg) : 0;
    void this.leaderboard.submit(this.playerName, score);
    this.input.stop();
    if (this.isMobile) hide(el('touch-controls'));
    const best = Math.max(this.leaderboard.best(), score);
    this.screens.showDeath(score, best);
  }

  private async openSkins(): Promise<void> {
    await this.skinsScreen.render();
    this.screens.showSkins();
  }

  private refreshPlayerSkin(): void {
    const player = this.world?.player;
    if (player) player.skinId = this.skins.selectedId();
  }

  // ===================== Frame loop =====================

  private loop = (now: number): void => {
    if (!this.running) return;
    const rawDt = now - this.lastFrameTime;
    this.lastFrameTime = now;
    const dtMs = Math.min(rawDt, MAX_FRAME_MS);
    const dtSec = dtMs / 1000;
    if (dtSec > 0) this.fps = lerpScalar(this.fps, 1 / dtSec, 0.1);

    if (this.world && this.sim) {
      // 1) Input -> player intent (only while actually playing).
      const player = this.world.player;
      if (this.state === 'playing' && player && player.alive) {
        applyFrameToIntent(player, this.input.drain());
      } else {
        this.input.drain(); // discard input accumulated while not playing
      }

      // 2) Advance the deterministic simulation, then let the net boundary sync.
      this.sim.advance(dtMs);
      this.network.sync(this.world);

      // 3) Camera follows the interpolated player position. Manual zoom (view
      //    only) is applied here, never through the sim input path.
      const cam = this.world.player;
      if (cam) {
        if (this.zoomInput) this.camera.nudgeManualZoom(this.zoomInput.drain());
        const a = this.sim.alpha;
        const ix = lerpScalar(cam.prevPos.x, cam.pos.x, a);
        const iy = lerpScalar(cam.prevPos.y, cam.pos.y, a);
        this.camera.update(ix, iy, cam.size, dtSec);
      }

      // 4) Render.
      this.renderer.render({
        world: this.world,
        camera: this.camera.state,
        alpha: this.sim.alpha,
        colorFor: this.colorFor,
        debug: FEATURES.debugOverlay
          ? `fps ${Math.round(this.fps)} · entities ${this.world.entities.length}`
          : null,
      });

      // 5) HUD + death handling.
      if (this.state === 'playing') {
        if (now - this.lastHudTime > HUD_INTERVAL_MS) {
          this.updateHud();
          this.lastHudTime = now;
        }
        if (player && !player.alive) this.handleDeath();
      }
    }

    requestAnimationFrame(this.loop);
  };

  private colorFor = (e: Entity): string => {
    if (e.id === this.world?.playerId) return this.skins.colorFor(e.skinId);
    // Stable, varied hue per bot id.
    const hue = (e.id * 47) % 360;
    return `hsl(${hue}, 62%, 56%)`;
  };

  private updateHud(): void {
    if (!this.world) return;
    const now = this.world.time;
    const player = this.world.player;
    // Rank everyone by SCORE (not raw size) so the board matches the numbers it
    // shows and stays ordered even when blobs pile up at the size cap.
    const scored = this.world.entities
      .filter((e) => e.alive)
      .map((e) => ({ e, score: entityScore(e, now, this.cfg) }))
      .sort((a, b) => b.score - a.score);
    const rank = player ? scored.findIndex((s) => s.e.id === player.id) + 1 : 0;
    const rows: HudLeaderboardRow[] = scored.slice(0, this.cfg.leaderboard.size).map((s) => ({
      name: s.e.name,
      score: s.score,
      isMe: s.e.id === this.world?.playerId,
    }));
    this.hud.update({
      size: player ? player.size : 0,
      score: player ? entityScore(player, now, this.cfg) : 0,
      rank: rank || scored.length,
      total: scored.length,
      rows,
    });
  }

  private onResize = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.resize(window.innerWidth, window.innerHeight, dpr);
  };
}
